import {
  summaryOptions,
  useChangeViewOptions,
} from "@scenes/searchResults/Utils";
import CustomTopTabs from "@components/molecules/CustomTopTabs/CustomTopTabs";
import SearchResultSummaryBox from "@components/molecules/SearchResultSummaryBox";
import { CheckBox } from "@components/atoms";
import RenderHTML from "react-native-render-html";
import { useTypographyStore } from "@redux/slices/typographySlice";
import Animated, { useSharedValue } from "react-native-reanimated";
import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { ProductCategory } from "@utils/constants";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import currency from "currency.js";
import { useScrollContext } from "@navigations/utils/ScrollContext";
import Routes from "@navigations/Routes";
import SortingFiltersModal from "@scenes/searchResults/SortingFiltersModal";
import SavedSearchPopup from "@scenes/searchResults/SavedSearchPopup";
import { useMasterCheckBoxAnimatedStyle } from "@scenes/searchResults/types";
import { useTheme } from "styled-components/native";
import { getDrawerWidth } from "@navigations/utils/constants";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import { useDispatch, useSelector } from "react-redux";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import { useCustomSearchData } from "@redux/slices/searchResultsCustomization";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { normalizeBoolean } from "@components/atoms/Filters/ButtonSection/Utils";
import BulkActionsStaticButton from "@components/atoms/BulkActionsStaticButton";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import {
  ChangeViewMenuOptions,
  GemstoneChangeViewMenuOptions,
  initialProductArray,
  jewelryInitialProductArray,
} from "@utils/product/menuBuilders";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";
import { navigate, push, replace } from "@navigations/utils/RootNavigation";
import { filterSummaryData } from "@scenes/bulk-request/Utils";
import { useHideSummarySectionProduct } from "@redux/slices/globalSlice";

const ListHeaderComponent = ({
  selectViewType,
  sortingFiltersParams,
  productType,
  masterCheckBoxSelection,
  selectedItems,
  selectedViewType,
  totalItems,
  totalItemsPages,
  setSortingFiltersParams,
  toggleFilterScreen,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
  queryParamsList,
  masterCheckboxSelected,
  isFilterVisible,
  showDotOnFilter,
  selectOption,
  enterSecondFlow = false,
  fromBarcodeScanner = false,
  floatingButtonClick,
  products,
  isIFrame,
  enableMobileWebView,
}: any) => {
  const mEnterSecondFlow = normalizeBoolean(enterSecondFlow);
  const userInfo = useUserInfo();
  const dispatch = useDispatch();
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const { deviceWidth: screenWidth } = useIsFoldable();
  const searchResultContainerWidth =
    screenWidth - getDrawerWidth(screenWidth, theme);
  const productPrefix = useProductPrefix();
  const [scrollValue] = useScrollContext();
  const isTablet = theme.isLargeScreen || theme.isTablet;
  const [_, __, ___, _____, ______, summaryBoxAnimatedStyle] =
    useScrollContext();
  const isGuest = useSelector((state: any) => state.userInfo.isGuest);
  const customSearchData = useCustomSearchData();
  const summarySectionData = filterSummaryData(
    sectionName === SearchFilterTypeEnum.PAIRS
      ? customSearchData[productType]?.summarySection?.pair?.options
      : sectionName === SearchFilterTypeEnum.PARCEL
      ? customSearchData[productType]?.summarySection?.parcels?.options
      : customSearchData[productType]?.summarySection?.options,
    userInfo.getPriceMode()
  );
  const hideSummarySectionProduct = useHideSummarySectionProduct();
  const hideSummarySection = hideSummarySectionProduct?.[productType] ?? false;
  const changeViewOptions = useChangeViewOptions(
    customSearchData[productType]?.header[`sub-sections`]?.change_view?.options,
    userInfo.getPriceMode(),
    t
  );
  const [openSortingFilterDialog, setOpenSortingFilterDialog] = useState(false);
  const [isSavedSearchPopupOpen, setIsSavedSearchPopupOpen] = useState(false);

  const [productArray, setProductArray] = useState(
    productType === ProductCategory.JEWELRY
      ? jewelryInitialProductArray(t)
      : initialProductArray(t)
  );

  useEffect(() => {
    setProductArray(
      productType === ProductCategory.JEWELRY
        ? jewelryInitialProductArray(t)
        : initialProductArray(t)
    );
  }, [userInfo.user?.locale, productType]);

  const selectedItemsContainerWidth = () => {
    switch (selectedViewType) {
      case SearchResultsAndFavoritesEnum.LIST:
      case SearchResultsAndFavoritesEnum.LIST_EXPANDED:
      default:
        return theme.isMobile
          ? screenWidth - 16
          : searchResultContainerWidth - 32;
    }
  };

  const checkBoxContainerWidth = useSharedValue(selectedItemsContainerWidth());
  const masterCheckBoxAnimatedStyle = useMasterCheckBoxAnimatedStyle({
    yOffset: scrollValue,
    checkBoxContainerWidth,
    mSelectedViewType: selectedViewType,
  });

  const allowedItems = `${totalItemsPages ? 1 : 0}-${totalItemsPages}`;

  const selectAllText = t("searchResult.selectAll.text", {
    x: allowedItems,
    y: currency(totalItems, {
      symbol: "",
      precision: 0,
    }).format(),
  });

  const deSelectAllText = t("searchResult.deSelectAll.text", {
    x: allowedItems,
    y: currency(totalItems, {
      symbol: "",
      precision: 0,
    }).format(),
  });

  const filteredChangeViewOptions = !enableMobileWebView
    ? changeViewOptions
    : changeViewOptions.filter(
        (option: any) =>
          option.name !== SearchResultsAndFavoritesEnum.LIST_EXPANDED
      );

  const changeViewMenuOptions =
    filteredChangeViewOptions.length > 0
      ? filteredChangeViewOptions
      : productType === ProductCategory.GEMSTONE
      ? GemstoneChangeViewMenuOptions(t)
      : ChangeViewMenuOptions(t, enableMobileWebView);

  const deselectAllOptions = () => {
    const updatedProductArray = productArray.map((item) => ({
      ...item,
      selection: false,
    }));
    setProductArray(updatedProductArray);
  };

  const handleCardClick = (clickedItem: any) => {
    const updatedProductArray = productArray.map((item) =>
      item.id === clickedItem.id
        ? { ...item, selection: true }
        : { ...item, selection: false }
    );
    setProductArray(updatedProductArray);
    switch (clickedItem.eventName) {
      case SearchResultsAndFavoritesEnum.SAVE_SEARCH:
        if (userInfo?.isGuest) {
          dispatch(setLoginModalType("default"));
        } else {
          setIsSavedSearchPopupOpen(true);
        }
        break;
      case SearchResultsAndFavoritesEnum.FILTERS:
        if (userInfo.isIFrame()) {
          replace(Routes.FilterScreen, {
            productType,
            fromResultScreen: false,
            sectionName,
            productPrefix,
            enterSecondFlow: mEnterSecondFlow,
          });
          deselectAllOptions();
        } else if (theme.isLargeScreen || theme.isTablet) {
          toggleFilterScreen();
          deselectAllOptions();
        } else {
          const filterParams = {
            productType,
            fromResultScreen: true,
            sectionName,
            productPrefix,
            enterSecondFlow: mEnterSecondFlow,
          };
          // Stone → setting pushes setting search on top of the stone filter.
          // Popping to FilterScreen would remove that search and leave the builder.
          if (mEnterSecondFlow) {
            push(Routes.FilterScreen, filterParams);
          } else {
            navigate(Routes.FilterScreen, filterParams, {
              merge: true,
              pop: true,
            });
          }
        }
        break;
      case SearchResultsAndFavoritesEnum.SORTING:
        setOpenSortingFilterDialog(true);
        break;
    }
  };

  const handleSavedSearchPopupShowChange = (show: boolean) => {
    setIsSavedSearchPopupOpen(show);
    deselectAllOptions();
  };

  const onSortingFiltersApplied = (paramList: string[]) => {
    setSortingFiltersParams((prev) => ({
      ...prev,
      [productType]: paramList, // Dynamically set values for the given productType
    }));
  };
  return (
    <>
      <CustomTopTabs
        productArray={productArray}
        fromBarcodeScanner={fromBarcodeScanner}
        handleCardClick={handleCardClick}
        flatListBackgroundColor={theme["core-01"]}
        menuOptions={changeViewMenuOptions}
        defaultViewType={selectedViewType}
        selectViewType={selectViewType}
        menuItemEventName={SearchResultsAndFavoritesEnum.CHANGE_VIEW}
        sortingFiltersParams={sortingFiltersParams}
        showDotOnFilter={showDotOnFilter}
        isFilterVisible={isFilterVisible}
        sectionName={sectionName}
        showChangeViewMenu={
          changeViewOptions.length > 1 &&
          Object.keys(
            customSearchData[productType]?.header[`sub-sections`]?.change_view
              ?.options
          ).length > 1
        }
        disableAnimations
        totalItems={totalItems}
        fromSearchResultScreen={true}
      />
      {!isGuest &&
        !hideSummarySection &&
        selectedItems?.length > 0 &&
        ![ProductCategory.JEWELRY, ProductCategory.LABGROWN_JEWELRY].includes(
          productType
        ) && (
          <Animated.View style={summaryBoxAnimatedStyle}>
            <SearchResultSummaryBox
              summaryOptions={summaryOptions(
                userInfo?.user?.price_mode,
                productType,
                userInfo?.isVdbMarketPlace(),
                summarySectionData
              )}
              selectedItems={selectedItems}
              productType={productType}
              tabletScreenWidth={screenWidth}
              selectOption={selectOption}
              isFilterVisible={isFilterVisible}
              sectionName={sectionName}
            />
          </Animated.View>
        )}

      <Animated.View
        style={[
          !theme.isMobile &&
            !isFilterVisible && {
              minWidth: screenWidth - SpaceType.Double * 2,
            },
          styles.masterCheckboxContainerStyle,
          masterCheckBoxAnimatedStyle,
        ]}
      >
        {(!isTablet ||
          (selectedViewType !== SearchResultsAndFavoritesEnum.LIST &&
            isTablet)) &&
          !userInfo.isIFrame() && (
            <CheckBox
              fillColor={theme["knob-bor"]}
              checkBoxSize={20}
              checkedFunction={masterCheckBoxSelection}
              isChecked={masterCheckboxSelected}
            />
          )}
        <View style={styles.renderHTMLContainer}>
          <RenderHTML
            contentWidth={screenWidth}
            source={{
              html: `${
                masterCheckboxSelected ? deSelectAllText : selectAllText
              }`,
            }}
            tagsStyles={{
              body: {
                ...typography["Minor-R"],
                color: theme["core-06"],
              },
            }}
          />
        </View>
        {products?.length > 0 && !theme.isLargeScreen && !isIFrame() && (
          <BulkActionsStaticButton
            title={t("searchResult.bulkActions.button.text")}
            buttonWidth={125}
            floatingButtonClick={floatingButtonClick}
            bottomMargin={0}
            isExpanded={true}
          />
        )}
      </Animated.View>

      <SortingFiltersModal
        isOpen={openSortingFilterDialog}
        onClose={() => {
          deselectAllOptions();
          setOpenSortingFilterDialog((open) => !open);
        }}
        productType={productType}
        onApply={onSortingFiltersApplied}
        selectedSortingFilters={sortingFiltersParams}
        sectionName={sectionName}
      />

      {isSavedSearchPopupOpen && (
        <SavedSearchPopup
          onSavedSearchPopupShowChange={handleSavedSearchPopupShowChange}
          queryParamList={queryParamsList}
          productType={productType}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  masterCheckboxContainerStyle: {
    flexDirection: "row",
    minHeight: 50,
    alignItems: "center",
    justifyContent: "flex-start",
    alignSelf: "center",
  },
  renderHTMLContainer: {
    flex: 1,
    flexShrink: 1,
  },
});

export default memo(ListHeaderComponent);
